/**
 * Event rendering. The rules log is a rendering of GameEvents, in the style of
 * BLUEPRINT section 16: "Turn 6. Player A generated 3 Solana Gas."
 *
 * Keep every line one sentence, past tense, with the turn number first.
 */

import type { Chain, Keyword } from './cardTypes';
import type { GameEvent, GameState } from './types';

export type NameMap = Record<string, string>;

const GAS_NAME: Record<Chain | 'generic', string> = {
  generic: 'generic Gas',
  btc: 'Bitcoin Gas',
  eth: 'Ethereum Gas',
  sol: 'Solana Gas',
  bnb: 'BNB Gas',
  xrp: 'XRP Gas',
};

const KEYWORD_NAME: Record<Keyword, string> = {
  vamp: 'VAMP',
  drainerLink: 'DRAINER LINK',
  ape: 'APE',
  offChain: 'OFF-CHAIN',
  oracle: 'ORACLE',
  coldStorage: 'COLD STORAGE',
  hodl: 'HODL',
  uptime: 'UPTIME',
  raid: 'RAID',
  slippage: 'SLIPPAGE',
  frontrun: 'FRONTRUN',
  staked: 'STAKED',
};

const PHASE_NAME: Record<string, string> = {
  setup: 'Setup',
  sync: 'Sync',
  draw: 'Draw',
  main1: 'Main 1',
  combat: 'Combat',
  main2: 'Main 2',
  end: 'End',
  over: 'Game over',
};

const STEP_NAME: Record<string, string> = {
  declareAttackers: 'Declare attackers',
  attackersDeclared: 'Attackers declared',
  declareBlockers: 'Declare blockers',
  blockersDeclared: 'Blockers declared',
  frontrunDamage: 'Frontrun damage',
  regularDamage: 'Regular damage',
  combatEnd: 'End of combat',
};

export function nameMapFor(state: GameState): NameMap {
  const map: NameMap = {};
  for (const pid of state.playerOrder) map[pid] = state.players[pid].name;
  return map;
}

function who(names: NameMap, id: string): string {
  return names[id] ?? id;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * One log line for one event, or null for events that are bookkeeping only.
 * The turn prefix is added by renderEvent so single lines read on their own.
 */
export function renderEvent(event: GameEvent, names: NameMap = {}): string | null {
  const body = renderBody(event, names);
  if (body === null) return null;
  return event.turn > 0 ? `Turn ${event.turn}. ${body}` : body;
}

function renderBody(event: GameEvent, names: NameMap): string | null {
  const p = 'player' in event ? who(names, event.player) : '';
  switch (event.type) {
    case 'GameCreated':
      return `The match began with seed ${event.seed}. ${who(names, event.startingPlayer)} goes first.`;
    case 'HandDrawn':
      return `${p} drew an opening hand of ${event.count}.`;
    case 'HandRedrawn':
      return `${p} took a Redraw and drew ${event.count}.`;
    case 'HandKept':
      return `${p} kept their opening hand.`;
    case 'TurnStarted':
      return `${p} started their turn.`;
    case 'PhaseChanged':
      return `${p} entered ${PHASE_NAME[event.phase] ?? event.phase}.`;
    case 'CombatStepChanged':
      return `${STEP_NAME[event.step] ?? event.step}.`;
    case 'CardDrawn':
      return `${p} drew ${event.cardName}.`;
    case 'DeckExhausted':
      return `${p} could not draw ${plural(event.amount, 'card')} and took deck exhaustion.`;
    case 'CardsSynced':
      return event.instanceIds.length === 0
        ? null
        : `${p} synced ${plural(event.instanceIds.length, 'card')}.`;
    case 'Unfroze':
      return `${event.name} thawed and stayed exhausted.`;
    case 'GasGenerated':
      return `${p} generated ${event.amount} ${GAS_NAME[event.chain]}.`;
    case 'GasSpent':
      return `${p} paid for ${event.cardName}.`;
    case 'GasPoolEmptied':
      return null;
    case 'WalletInstalled':
      return `${p} installed ${event.name}.`;
    case 'CardPlayed':
      return `${p} played ${event.name}.`;
    case 'AbilityActivated':
      return event.label
        ? `${p} activated ${event.name}: ${event.label}.`
        : `${p} activated an ability of ${event.name}.`;
    case 'TriggerFired':
      return `${event.name} triggered on ${event.event}.`;
    case 'StackItemResolved':
      return `${event.label} resolved.`;
    case 'Reverted':
      return `${p} reverted ${event.label}.`;
    case 'Fizzled':
      return `${event.label} fizzled with no legal targets.`;
    case 'Deployed':
      return `${p} deployed ${event.name}.`;
    case 'TokenCreated':
      return `${p} created a ${event.name} token.`;
    case 'TokenCeased':
      return `${event.name} ceased to exist.`;
    case 'UpgradeAttached':
      return `${event.name} was attached to ${event.hostName}.`;
    case 'Destroyed':
      return `${event.name} was destroyed by ${event.reason}.`;
    case 'Died':
      return `${event.name} went to the Dust.`;
    case 'Sacrificed':
      return `${p} sacrificed ${event.name}.`;
    case 'Bounced':
      return `${event.name} was returned to its owner's hand.`;
    case 'Blacklisted':
      return `${event.name} was blacklisted.`;
    case 'Recovered':
      return `${p} recovered ${event.name} to their ${event.to === 'hand' ? 'hand' : 'battlefield'}.`;
    case 'Discarded':
      return `${p} discarded ${event.name}.`;
    case 'Milled':
      return `${p} milled ${plural(event.count, 'card')}.`;
    case 'Scried':
      return `${p} scried ${event.look} and put ${event.toBottom} on the bottom.`;
    case 'WalletFetched':
      return event.instanceId
        ? `${p} fetched ${event.name} from their deck.`
        : `${p} searched for a Wallet and found nothing.`;
    case 'DamageDealt':
      return `${event.sourceName} dealt ${plural(event.amount, 'damage', 'damage')} to ${event.targetName}.`;
    case 'DamageDealtToPlayer':
      return `${event.sourceName} dealt ${plural(event.amount, 'damage', 'damage')} to ${who(
        names,
        event.targetPlayer,
      )}.`;
    case 'IntegrityChanged':
      return event.delta > 0
        ? `${p} restored ${event.delta} Integrity and is at ${event.total}.`
        : `${p} lost ${-event.delta} Integrity and is at ${event.total}.`;
    case 'VampRestored':
      return `${event.sourceName} drained ${event.amount} with VAMP.`;
    case 'Buffed':
      return `${event.name} got ${signed(event.attack)}/${signed(event.health)}${
        event.duration === 'turn' ? ' until end of turn' : ''
      }.`;
    case 'KeywordGranted':
      return `${event.name} gained ${KEYWORD_NAME[event.keyword]}.`;
    case 'KeywordRemoved':
      return `${event.name} lost ${KEYWORD_NAME[event.keyword]}.`;
    case 'Exhausted':
      return `${event.name} was exhausted.`;
    case 'Readied':
      return `${event.name} readied.`;
    case 'Frozen':
      return `${event.name} was frozen.`;
    case 'AttackersDeclared':
      return `${p} attacked with ${event.attackers.map((a) => a.name).join(', ')}.`;
    case 'BlockersDeclared':
      return event.blocks.length === 0
        ? `${p} declared no blockers.`
        : `${p} blocked ${event.blocks
            .map((b) => `${b.attackerName} with ${b.blockerName}`)
            .join(', ')}.`;
    case 'LegendaryRuleApplied':
      return `${event.name} went to the Dust for the Legendary rule.`;
    case 'HandSizeDiscard':
      return `${p} must discard ${plural(event.count, 'card')} down to the hand size limit.`;
    case 'EffectsExpired':
      return null;
    case 'PriorityChanged':
      return null;
    case 'Passed':
      return null;
    case 'Conceded':
      return `${p} conceded.`;
    case 'PlayerLost':
      return `${p} lost (${event.reason}).`;
    case 'GameEnded':
      return event.draw
        ? 'The match ended in a draw.'
        : `${who(names, event.winner ?? '')} won the match.`;
    case 'DevAction':
      return `Test Lab: ${p} ${event.detail}.`;
    default: {
      const never: never = event;
      throw new Error(`Unrendered event: ${JSON.stringify(never)}`);
    }
  }
}

function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

/** The whole match log, bookkeeping events dropped. */
export function renderLog(state: GameState): string[] {
  const names = nameMapFor(state);
  const out: string[] = [];
  for (const event of state.eventLog) {
    const line = renderEvent(event, names);
    if (line) out.push(line);
  }
  return out;
}

/** Just the lines a single action produced, for the UI toast strip. */
export function renderEvents(events: GameEvent[], state: GameState): string[] {
  const names = nameMapFor(state);
  const out: string[] = [];
  for (const event of events) {
    const line = renderEvent(event, names);
    if (line) out.push(line);
  }
  return out;
}
